import { BACKEND_ERROR_CODE, createFlatRequest, createRequest } from '@sa/axios';
import { useAuthStore } from '@/store/modules/auth';
import { localStg } from '@/utils/storage';
import { getServiceBaseURL } from '@/utils/service';
import { $t } from '@/locales';
import { getCookie } from '@/utils/common';
// import { useMessage } from 'naive-ui';

const isHttpProxy = import.meta.env.DEV && import.meta.env.VITE_HTTP_PROXY === 'Y';
const { baseURL, otherBaseURL } = getServiceBaseURL(import.meta.env, isHttpProxy);

interface InstanceState {
    /** whether the request is refreshing token */
    isRefreshingToken: boolean;
}

export const request = createFlatRequest<App.Service.Response, InstanceState>(
    {
        baseURL,
        headers: {
            'Content-Type': 'application/json'
        },
        timeout: 100000, // 100 second timeout
        // Disable axios-retry to prevent timeout delays on errors
        'axios-retry': {
            retries: 0
        }
    },
    {
        async onRequest(config) {
            const { headers } = config;

            // set token
            // const token = localStg.get('token');
            // const Authorization = token ? `Bearer ${token}` : null;
            const csrfToken = getCookie('csrftoken');
            if (csrfToken) {
                Object.assign(headers, { 'X-CSRFToken': csrfToken });
            }

            return config;
        },

        isBackendSuccess(response) {
            // when the backend response status is 2xx, it means the request is success
            // 200 = OK, 201 = Created, 204 = No Content, etc.
            return response.status >= 200 && response.status < 300;
        },

        async onBackendFail(response, instance) {
            const authStore = useAuthStore();

            function handleLogout() {
                authStore.resetStore();
            }

            function logoutAndCleanup() {
                handleLogout();
                window.removeEventListener('beforeunload', handleLogout);
            }

            // when the backend response code is in `logoutCodes`, it means the user will be logged out and redirected to login page
            const logoutCodes = import.meta.env.VITE_SERVICE_LOGOUT_CODES?.split(',') || [];
            if (logoutCodes.includes(response.data.code)) {
                handleLogout();
                return null;
            }

            // when the backend response code is in `modalLogoutCodes`, it means the user will be logged out by displaying a modal
            const modalLogoutCodes = import.meta.env.VITE_SERVICE_MODAL_LOGOUT_CODES?.split(',') || [];
            if (modalLogoutCodes.includes(response.data.code)) {
                // prevent the user from refreshing the page
                window.addEventListener('beforeunload', handleLogout, { passive: true });

                window.$dialog?.error({
                    title: 'Error',
                    content: response.data.msg,
                    positiveText: $t('common.confirm'),
                    maskClosable: false,
                    onPositiveClick() {
                        logoutAndCleanup();
                    },
                    onClose() {
                        logoutAndCleanup();
                    }
                });

                return null;
            }


            return null;
        },
        transformBackendResponse(response) {
            return response.data;
        },
        onError(error) {
            // Handle errors immediately without delay
            // dont use this error.message; heer
            let backendErrorCode = '404';

            // get backend error message and code
            if (error.code === BACKEND_ERROR_CODE) {
                // message = error.response?.data?.msg || message;
                backendErrorCode = error.response?.data?.code || '';
            }

            // Enhanced 429 rate limiting error handling
            if (error.response?.status === 429) {
                const metadata = error.config?.metadata;
                const operationType = metadata?.operationType || 'read';
                const isUserInitiated = metadata?.isUserInitiated ?? true;
                const showUserErrors = metadata?.showUserErrors ?? false;

                // Create enhanced error object with metadata
                const rateLimitError: App.Request.RateLimitError = {
                    status: 429,
                    message: 'Rate limit exceeded',
                    operationType,
                    isUserInitiated
                };

                // Attach enhanced error info and statusCode to the original error for component-level handling
                error.rateLimitInfo = rateLimitError;
                error.statusCode = 429;

                // If showUserErrors is true, re-throw to let component handle the error display
                if (showUserErrors) {
                    throw error;
                }

                // Silent handling for read operations (polling)
                return;
            }

            // the error message is displayed in the modal
            const modalLogoutCodes = import.meta.env.VITE_SERVICE_MODAL_LOGOUT_CODES?.split(',') || [];
            if (modalLogoutCodes.includes(backendErrorCode)) {
                return;
            }

            // For client errors (4xx), check if this is a user-initiated operation that should show errors
            if (error.response?.status && error.response.status >= 400 && error.response.status < 500) {
                const metadata = error.config?.metadata;
                const showUserErrors = metadata?.showUserErrors ?? false;

                // For user-initiated operations that should show errors, re-throw to let caller handle.
                // The flat request wrapper will catch this and expose it as { error, statusCode }
                // so the caller can map 400/401/403/404 to specific user-facing messages.
                if (showUserErrors) {
                    throw error;
                }
                
                // For silent operations, just return
                return;
            }

            // // Only show error messages for server errors or network issues
            // if (!error.response || error.response.status >= 500) {
            //   window.$message?.error?.(message);
            // }
        }
    }
);

export const demoRequest = createRequest<App.Service.DemoResponse>(
    {
        baseURL: otherBaseURL.demo
    },
    {
        async onRequest(config) {
            const { headers } = config;

            // set token
            const token = localStg.get('token');
            const Authorization = token ? `Bearer ${token}` : null;
            Object.assign(headers, { Authorization });

            return config;
        },
        isBackendSuccess(response) {
            // when the backend response code is "200", it means the request is success
            // you can change this logic by yourself
            return response.data.status === '200';
        },
        async onBackendFail(_response) {
            // when the backend response code is not "200", it means the request is fail
            // for example: the token is expired, refresh token and retry request
        },
        transformBackendResponse(response) {
            return response.data.result;
        },
        onError(error) {
            // when the request is fail, you can show error message

            let message = error.message;

            // show backend error message
            if (error.code === BACKEND_ERROR_CODE) {
                message = error.response?.data?.message || message;
            }

            window.$message?.error(message);
        }
    }
);
